import { extractCountryCode } from '@components/common';
import {
  CategoriesList,
  commonMetas,
  remoteJobsList,
} from '@components/common/constant';
import Metacategories from '@components/common/metaJson/category.json';
import { SiteNavigationSchemaScript } from '@components/metaComponent/siteNavigationSchema';
import { BaseURL, isLowerEnv, isProdEnv, stripHtml } from 'helper';

interface CompanyPageMetaProps {
  company_details?: {
    name?: string;
    summary?: string;
    slug?: string;
    logo?: string;
    website?: string;
    city?: string;
    state?: string;
    country?: string;
  };
  companyqna?: Array<{
    question: string;
    answer: string;
  }>;
  companyname?: string;
  categoriesType?: any;
  seo?: {
    description?: string;
  };
  forceIndex?: boolean;
}

export const RemoteCategoriesMetaTags = (props: any) => {
  // Collect all the generated meta tags
  const allMetaTags = Metacategories.flatMap((obj: any) => {
    // Get the first (and only) key of each object
    const key =
      Object.keys(obj).find((item) => item == props.categoryName) || '';
    // Access the nested object using that key and create meta tags
    return key && createMetaTags({ ...commonMetas[0], ...obj[key] }, props);
  });
  const index = allMetaTags.findIndex((m) => m.key == 'robots');
  if (index == -1) {
    allMetaTags.push(
      <meta
        name="robots"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />
    );
  }
  if (props.isQueryParamsEmpty != undefined && !props.isQueryParamsEmpty) {
    //remove robots meta tag from the allMetaTags that has index and follow then add no follow and index
    if (index > -1) allMetaTags.splice(index, 1);
    allMetaTags.push(<meta name="robots" content="noindex, nofollow" />);
  }
  // Return the meta tags as JSX elements
  return <>{allMetaTags}</>;
};

const refreshWebPageDateModified = (
  schema: any,
  schemaDateModified?: string
): any => {
  const updateNode = (node: any): any => {
    if (Array.isArray(node)) {
      return node.map(updateNode);
    }

    if (!node || typeof node !== 'object') {
      return node;
    }

    const updatedNode: Record<string, any> = {};

    Object.keys(node).forEach((key) => {
      updatedNode[key] = updateNode(node[key]);
    });

    const nodeType = updatedNode['@type'];
    const isWebPageType =
      nodeType === 'WebPage' ||
      (Array.isArray(nodeType) && nodeType.includes('WebPage'));

    if (isWebPageType && schemaDateModified) {
      updatedNode.dateModified = schemaDateModified;
    }

    return updatedNode;
  };

  return updateNode(schema);
};

function createMetaTags(data: { [key: string]: any }, props: any): any[] {
  const metaTags: any[] = [];
  // Loop over the properties of the data object
  for (const key in data) {
    if (Object.hasOwn(data, key)) {
      if (key === 'preconnect') {
        data[key].map((item: any) => {
          metaTags.push(
            <link rel="preconnect" href={item} crossOrigin="anonymous" />
          );
        });
      } else if (key === 'canonical' && !props.canonicalTwo && isProdEnv) {
        const baseURL = `${BaseURL}${data[key]}`;
        metaTags.push(<link rel="canonical" href={baseURL} />);
      } else if (key === 'canonicalTwo' && props.canonicalTwo && isProdEnv) {
        metaTags.push(<link rel="canonical" href={data['canonicalTwo']} />);
      } else if (key === 'title') {
        metaTags.push(<title key="title">{data[key]}</title>);
        /***remote-jobs all pages are affected. So this meta tag remove 
        metaTags.push(<meta name={key} content={String(data[key])} />);**/
      } else if (key === 'yoast-schema') {
        // Create script tag for yoast-schema
        const schema = refreshWebPageDateModified(
          data[key],
          props.schemaDateModified
        );
        metaTags.push(
          <script
            type="application/ld+json"
            className="yoast-schema-graph"
            key="yoast-schema"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
          />
        );
        metaTags.push(SiteNavigationSchemaScript());
        if (props.categoryName === 'index') {
          metaTags.push(
            createCollectionPageSchemaTag(props.categorizedJobs || [])
          );
        } else if (props.categoryName && props.jobCardData) {
          metaTags.push(
            createCategoryCollectionPageSchemaTag({
              slug: props.categoryName,
              title: props.categoryTitle || '',
              description: props.categoryDescription || '',
              jobs: props.jobCardData,
            })
          );
        }
      } else if (typeof data[key] === 'object' && data[key] !== null) {
        // Recursively handle nested objects
        for (const nestedKey in data[key]) {
          if (Object.hasOwn(data[key], nestedKey)) {
            metaTags.push(
              <meta
                name={`${key}:${nestedKey}`}
                content={String(data[key][nestedKey])}
                key={`${key}:${nestedKey}`}
              />
            );
          }
        }
      } else if (
        ![
          'canonicalTwo',
          'canonical',
          ...(isLowerEnv ? ['robots'] : []),
        ].includes(key)
      ) {
        // Create meta tag for non-object properties
        metaTags.push(
          <meta name={key} content={String(data[key])} key={key} />
        );
      }
    }
  }
  return metaTags;
}

export const CompanyPageMetaTags = (props: CompanyPageMetaProps) => {
  const { company_details, companyname, seo, categoriesType, forceIndex } =
    props;
  const companyName = company_details?.name || companyname || '';
  const companySlug = company_details?.slug || '';
  const companyDescription =
    seo?.description ||
    company_details?.summary ||
    `Find remote jobs at ${companyName}. Explore work-from-home roles in tech, support, marketing, and more—now hiring remote talent.`;

  // Determine which meta template to use based on company data availability
  const hasCompanySummary = company_details && company_details.summary;
  const metaType = categoriesType;

  // Determine robots directive based on conditions
  const robotsDirective =
    forceIndex || hasCompanySummary
      ? 'follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large'
      : 'noindex, nofollow';

  // Get the appropriate meta object
  const metaData = Metacategories.find((obj: any) => {
    return obj[metaType];
  });
  const metaDataFinal = metaData ? metaData?.company_page : null;

  if (!metaDataFinal) {
    return <></>;
  }

  // Handle country name conversion

  // Replace placeholders in the meta data
  const escapeJsonString = (str = '') =>
    str.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  const processedData = JSON.parse(
    JSON.stringify(metaDataFinal)
      .replaceAll('{BaseURL}', escapeJsonString(BaseURL || ''))
      .replaceAll('{companyName}', escapeJsonString(companyName))
      .replaceAll('{companySlug}', escapeJsonString(companySlug))
      .replaceAll('{companyDescription}', escapeJsonString(companyDescription))
      .replaceAll('{robotsDirective}', escapeJsonString(robotsDirective))
      .replaceAll(
        '{companyLogo}',
        company_details?.logo
          ? escapeJsonString(`${BaseURL || ''}${company_details.logo}`)
          : ''
      )
      .replaceAll(
        '{companyWebsite}',
        escapeJsonString(company_details?.website || '')
      )
      .replaceAll(
        '{companyCity}',
        escapeJsonString(company_details?.city || '')
      )
      .replaceAll(
        '{companyState}',
        escapeJsonString(company_details?.state || '')
      )
      .replaceAll(
        '{companyCountry}',
        escapeJsonString(extractCountryCode(company_details?.country) || '')
      )
      .replaceAll('"{companyQNA}"', '[]') // Keep this as-is
  );

  // Generate meta tags using the same logic as categories
  const allMetaTags = createCompanyMetaTags(processedData, props);

  return <>{allMetaTags}</>;
};

function createCompanyMetaTags(
  data: { [key: string]: any },
  props: CompanyPageMetaProps
): any[] {
  const metaTags: any[] = [];
  const companyName = props.company_details?.name || props.companyname || '';

  for (const key of Object.keys(data)) {
    if (!Object.hasOwn(data, key)) continue;

    switch (key) {
      case 'title':
        metaTags.push(<title key="title">{data[key]}</title>);
        break;
      case 'canonical':
        if (isProdEnv) {
          const baseURL = `${BaseURL}${data[key]}`;
          metaTags.push(
            <link rel="canonical" href={baseURL} key="canonical" />
          );
        }
        break;
      case 'og':
        metaTags.push(...createOgMetaTags(data[key]));
        break;
      case 'twitter':
        metaTags.push(...createTwitterMetaTags(data[key]));
        break;
      case 'article':
        metaTags.push(...createArticleMetaTags(data[key]));
        break;
      case 'yoast-schema':
        metaTags.push(createYoastSchemaTag(data[key], props, companyName));
        break;
      default:
        if (typeof data[key] === 'string') {
          metaTags.push(
            <meta name={key} content={String(data[key])} key={key} />
          );
        }
        break;
    }
  }

  return metaTags;
}

function createOgMetaTags(ogData: { [key: string]: any }) {
  return Object.keys(ogData)
    .filter((ogKey) => Object.hasOwn(ogData, ogKey))
    .map((ogKey) => (
      <meta
        property={`og:${ogKey}`}
        content={String(ogData[ogKey])}
        key={`og:${ogKey}`}
      />
    ));
}

function createTwitterMetaTags(twitterData: { [key: string]: any }) {
  return Object.keys(twitterData)
    .filter((twitterKey) => Object.hasOwn(twitterData, twitterKey))
    .map((twitterKey) => (
      <meta
        name={`twitter:${twitterKey}`}
        content={String(twitterData[twitterKey])}
        key={`twitter:${twitterKey}`}
      />
    ));
}

function createArticleMetaTags(articleData: { [key: string]: any }) {
  return Object.keys(articleData)
    .filter((articleKey) => Object.hasOwn(articleData, articleKey))
    .map((articleKey) => (
      <meta
        property={`article:${articleKey}`}
        content={String(articleData[articleKey])}
        key={`article:${articleKey}`}
      />
    ));
}

function createCollectionPageSchemaTag(categorizedJobs: any[] = []) {
  const BASE_URL = 'https://remote.co';
  const categoryMap = new Map(
    CategoriesList.map((cat) => [String(cat.id), cat])
  );

  const categories =
    categorizedJobs.length > 0
      ? categorizedJobs
      : remoteJobsList
          .map((id) => categoryMap.get(id))
          .filter((cat) => cat != null);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${BASE_URL}/remote-jobs#collectionpage`,
    name: "Remote Jobs in Today's Popular Categories - Remote.co",
    description:
      "Remote.co's job board has openings in the most popular remote job categories. Search for the best opportunities that match your skills.",
    url: `${BASE_URL}/remote-jobs`,
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${BASE_URL}/#website`,
      name: 'Remote.co',
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListOrder: 'https://schema.org/ItemListUnordered',
      numberOfItems: categories.length,
      itemListElement: categories.map((cat, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'CollectionPage',
          name: cat.heading,
          url: `${BASE_URL}/remote-jobs/${cat.slug}`,
        },
      })),
    },
  };

  return (
    <script
      type="application/ld+json"
      key="collection-page"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(schema, null, isProdEnv ? 0 : 2),
      }}
    />
  );
}

function createCategoryCollectionPageSchemaTag({
  slug,
  title,
  description,
  jobs,
}: {
  slug: string;
  title: string;
  description: string;
  jobs: any[];
}) {
  const BASE_URL = 'https://remote.co';
  const jobItems = jobs.slice(0, 8);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    '@id': `${BASE_URL}/remote-jobs/${slug}#collectionpage`,
    name: title,
    description,
    url: `${BASE_URL}/remote-jobs/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListOrder: 'https://schema.org/ItemListUnordered',
      numberOfItems: jobItems.length,
      itemListElement: jobItems.map((job, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: job.title || job.name || '',
        url: `${BASE_URL}/job-details/${job.slug || ''}`,
      })),
    },
  };


  return (
    <script
      type="application/ld+json"
      key="collection-page-category"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(schema, null, isProdEnv ? 0 : 2),
      }}
    />
  );
}

function createYoastSchemaTag(
  schema: any,
  props: CompanyPageMetaProps,
  companyName: string
) {
  let processedSchema = JSON.stringify(schema);

  // Check if we have valid FAQ data with actual questions and answers
  const hasValidFaqData =
    props.companyqna &&
    Array.isArray(props.companyqna) &&
    props.companyqna.length > 0 &&
    props.companyqna.some(
      (item) => item?.question?.trim() && item?.answer?.trim()
    );

  if (hasValidFaqData) {
    // Filter out invalid FAQ items and create proper schema
    const faqData =
      props.companyqna
        ?.filter((item) => item?.question?.trim() && item?.answer?.trim())
        .map((item: any) => ({
          '@type': 'Question',
          name: stripHtml(item.question),
          acceptedAnswer: {
            '@type': 'Answer',
            text: stripHtml(item.answer),
          },
        })) || [];

    // Only add FAQ if we have valid filtered data
    if (faqData.length > 0) {
      processedSchema = processedSchema
        .replaceAll(
          '"mainEntity":[]',
          `"mainEntity":${JSON.stringify(faqData)}`
        )
        .replaceAll('"{companyQNA}"', JSON.stringify(faqData))
        .replaceAll(
          '"mainEntity":"{companyQNA}"',
          `"mainEntity":${JSON.stringify(faqData)}`
        );
    } else {
      // Even if companyqna exists but has no valid items, remove FAQ schema
      processedSchema = processedSchema
        .replaceAll(
          /,\s*{\s*"@type"\s*:\s*"FAQPage"\s*,\s*"mainEntity"\s*:\s*[^}]*}\s*/g,
          ''
        )
        .replaceAll(
          /{\s*"@type"\s*:\s*"FAQPage"\s*,\s*"mainEntity"\s*:\s*[^}]*},?\s*/g,
          ''
        )
        .replaceAll('"{companyQNA}"', '[]')
        .replaceAll('"mainEntity":"{companyQNA}"', '"mainEntity":[]')
        .replaceAll(/,\s*,/g, ',')
        .replaceAll(/,\s*]/g, ']');
    }
  } else {
    // Remove the entire FAQPage schema block when no valid FAQ data is present
    processedSchema = processedSchema
      // Remove FAQPage with various patterns including empty arrays
      .replaceAll(
        /,\s*{\s*"@type"\s*:\s*"FAQPage"\s*,\s*"mainEntity"\s*:\s*\[\s*\]\s*}/g,
        ''
      )
      .replaceAll(
        /{\s*"@type"\s*:\s*"FAQPage"\s*,\s*"mainEntity"\s*:\s*\[\s*\]\s*},?\s*/g,
        ''
      )
      .replaceAll(
        /,\s*{\s*"@type"\s*:\s*"FAQPage"\s*,\s*"mainEntity"\s*:\s*"[^"]*"\s*}/g,
        ''
      )
      .replaceAll(
        /{\s*"@type"\s*:\s*"FAQPage"\s*,\s*"mainEntity"\s*:\s*"[^"]*"\s*},?\s*/g,
        ''
      )
      // Remove any remaining FAQ placeholders
      .replaceAll('"{companyQNA}"', '[]')
      .replaceAll('"mainEntity":"{companyQNA}"', '"mainEntity":[]')
      // Clean up syntax issues
      .replaceAll(/,\s*,/g, ',')
      .replaceAll(/,\s*]/g, ']');
  }

  processedSchema = processedSchema
    .replaceAll('{companyName}', companyName)
    .replaceAll('{BaseURL}', BaseURL || '')
    .replaceAll('{companySlug}', props.company_details?.slug || '')
    .replaceAll(
      '{companyLogo}',
      props.company_details?.logo
        ? `${BaseURL || ''}${props.company_details.logo}`
        : ''
    )
    .replaceAll('{companyWebsite}', props.company_details?.website || '')
    .replaceAll('{companyCity}', props.company_details?.city || '')
    .replaceAll('{companyState}', props.company_details?.state || '')
    .replaceAll(
      '{companyCountry}',
      extractCountryCode(props.company_details?.country) || ''
    );

  try {
    const schemaData = JSON.parse(processedSchema);
    return (
      <script
        type="application/ld+json"
        className="yoast-schema-graph"
        key="yoast-schema"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(schemaData),
        }}
      />
    );
  } catch (error) {
    console.error('Error processing yoast schema:', error);
    return (
      <script
        type="application/ld+json"
        className="yoast-schema-graph"
        key="yoast-schema"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
    );
  }
}
